import { useCallback } from 'react';
import { useMutation } from 'react-relay';
import * as Sentry from '@sentry/react';
import type { ExperimentsRecordExperimentsObservationMutation } from '../graphQLOperations/__generated__/ExperimentsRecordExperimentsObservationMutation.graphql';
import { recordExperimentObservation } from '../graphQLOperations/Experiments';
import { usePreviewMode } from '../contexts/PreviewModeContext';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';

/**
 * Broad family the metric belongs to. Stored in the `flag_name` column, which no
 * longer names a feature flag — enrollment lives in `experiments_flag_history`,
 * so which variant a user was in is resolved by joining on user and timestamp.
 */
export type ObservationCategory = 'affect_metrics' | 'skill_metrics' | 'engagement_metrics';

export interface Observation {
  /** What is being measured, within the category. Stored in `variant_name`. */
  subject: string;
  /** The specific datum. Stored in `observation_type`. */
  type: string;
  /** Defaults to 1, the useful default for "this happened" markers. */
  value?: number;
}

export interface ObservationLogResult {
  /** `type` of every observation that landed. */
  logged: string[];
  /** `type` of every observation that did not. Nothing is retried. */
  failed: string[];
}

/**
 * Writes rows to `experiments_observations`.
 *
 * Experiment-agnostic by design: a batch names its category, and each row a subject and a type,
 * never a feature flag. The user is resolved server-side from the auth context, so nothing here
 * identifies who the row is about.
 */
export const useObservationLogger = () => {
  const user = useAppSelector(selectUser);
  const previewMode = usePreviewMode();
  const [commitObservation] = useMutation<ExperimentsRecordExperimentsObservationMutation>(recordExperimentObservation);

  // Resolves false rather than rejecting, so one failed row can't take down the rest of a batch.
  const commit = useCallback(
    (category: ObservationCategory, { subject, type, value = 1 }: Observation) =>
      new Promise<boolean>(resolve => {
        commitObservation({
          variables: {
            input: {
              input: {
                flagName: category,
                variantName: subject,
                observationType: type,
                observation: value,
              },
            },
          },
          // A rejected row comes back as a payload error with the response otherwise intact, so
          // completion alone doesn't mean it landed. Reported here because nothing else sees it:
          // onError is already captured by the Relay network layer.
          onCompleted: response => {
            const errors = response.addExperimentsObservations?.errors;
            if (!errors?.length) return resolve(true);
            Sentry.captureException(
              new Error(`Failed to record observation ${type}: ${errors.map(error => error.message).join(', ')}`),
            );
            resolve(false);
          },
          onError: () => resolve(false),
        });
      }),
    [commitObservation],
  );

  // Teachers previewing a student view would otherwise write observations against their own account.
  const canLog = !previewMode && !!user?.id;

  const logObservations = useCallback(
    async (category: ObservationCategory, observations: Observation[]): Promise<ObservationLogResult> => {
      if (!canLog) return { logged: [], failed: observations.map(observation => observation.type) };

      const outcomes = await Promise.all(observations.map(observation => commit(category, observation)));

      return {
        logged: observations.filter((_, i) => outcomes[i]).map(observation => observation.type),
        failed: observations.filter((_, i) => !outcomes[i]).map(observation => observation.type),
      };
    },
    [canLog, commit],
  );

  return { logObservations };
};
